<script setup>
import { ref, computed, onMounted } from 'vue'
import { useWorkflowApi } from '../../../composables/useWorkflowApi.js'
import { useWorkflowCanvas } from '../../../composables/useWorkflowCanvas.js'
import WorkflowCanvas from '../../../components/communications/workflows/canvas/WorkflowCanvas.vue'
import NodePalette from '../../../components/communications/workflows/canvas/NodePalette.vue'
import NodeConfigPanel from '../../../components/communications/workflows/canvas/NodeConfigPanel.vue'
import FunnelOverlay from '../../../components/communications/workflows/canvas/FunnelOverlay.vue'
import { useNotificationStore } from '@/stores/NotificationStore.js';

const notificationStore = useNotificationStore();

const props = defineProps({
    workflowId: {
        type: [String, Number],
        required: true,
    },
})

const api = useWorkflowApi()
const workflowIdRef = ref(props.workflowId)

const canvas = useWorkflowCanvas(api, workflowIdRef)
const {
    nodes,
    edges,
    selectedNode,
    loadGraph,
    getGraphData,
    addNode,
    updateNodeConfig,
    selectNode,
    deselectNode,
    screenToFlowCoordinate,
} = canvas

const workflow = ref(null)
const workflowName = ref('')
const isSaving = ref(false)
const saveState = ref('')
const isPublishing = ref(false)
const publishState = ref('')
const isTogglingPause = ref(false)
const saveError = ref(null)

onMounted(async () => {
    try {
        const response = await api.fetchWorkflow(props.workflowId)
        const data = response?.data ?? response
        workflow.value = data
        workflowName.value = data.name ?? ''
        if (data.draft_graph_data) {
            loadGraph(data.draft_graph_data)
        }
    } catch (e) {
        console.error('Failed to load workflow:', e)
    }
})

async function saveDraft() {
    isSaving.value = true
    saveState.value = 'waiting'
    saveError.value = null
    try {
        await api.updateWorkflow(props.workflowId, { name: workflowName.value })
        await api.saveGraph(props.workflowId, getGraphData())
        saveState.value = 'success'
        return true
    } catch (e) {
        saveError.value = e.message
        saveState.value = 'error'
        return false
    } finally {
        isSaving.value = false
    }
}

async function publish() {
    isPublishing.value = true
    publishState.value = 'waiting'
    try {
        const draftSaved = await saveDraft()
        if (!draftSaved) {
            publishState.value = 'error'
            notificationStore.addNotification({
                title: 'Workflow could not be published',
                message: saveError.value,
                dismissAfter: 8000,
            });
            return
        }

        await api.publishWorkflow(props.workflowId)
        const response = await api.fetchWorkflow(props.workflowId)
        workflow.value = response?.data ?? response
        publishState.value = 'success'
    } catch (e) {
        saveError.value = e.message
        publishState.value = 'error'
        notificationStore.addNotification({
            title: 'Workflow could not be published',
            message: e.message,
            dismissAfter: 8000,
        });
    } finally {
        isPublishing.value = false
    }
}

async function togglePause() {
    if (!workflow.value) return

    isTogglingPause.value = true
    saveError.value = null

    try {
        if (workflow.value.status === 'paused') {
            await api.resumeWorkflow(props.workflowId)
            notificationStore.addNotification({
                title: 'Workflow activated',
                message: 'New enrollments will be processed.',
                dismissAfter: 5000,
            });
        } else {
            await api.pauseWorkflow(props.workflowId)
            notificationStore.addNotification({
                title: 'Workflow paused',
                message: 'Existing enrollments will continue to process, no new ones will be created.',
                dismissAfter: 5000,
            });
        }

        const response = await api.fetchWorkflow(props.workflowId)
        workflow.value = response?.data ?? response
    } catch (e) {
        saveError.value = e.message
    } finally {
        isTogglingPause.value = false
    }
}

function onNodeClick({ node }) {
    selectNode(node.id)
}

function onPaneClick() {
    deselectNode()
}

function onDrop(event) {
    const raw = event.dataTransfer.getData('application/workflow-node')
    if (!raw) return

    let item
    try {
        item = JSON.parse(raw)
    } catch {
        return
    }

    const position = screenToFlowCoordinate({
        x: event.clientX,
        y: event.clientY,
    })

    const newNode = addNode(item.type, position)

    // If it's a trigger with a subtype, set it immediately
    if (item.subtype && newNode) {
        updateNodeConfig(newNode.id, { trigger_type: item.subtype })
    }
}

function onConfigUpdate(config) {
    if (!selectedNode.value) return
    updateNodeConfig(selectedNode.value.id, config)
    // Refresh selected node reference
    selectNode(selectedNode.value.id)
}

const isPublished = computed(() => !!workflow.value?.active_version?.id)
const isPaused = computed(() => workflow.value?.status === 'paused')
const canTogglePause = computed(() =>
    ['active', 'paused'].includes(workflow.value?.status)
)
const pauseButtonLabel = computed(() => {
    if (isTogglingPause.value)
        return isPaused.value ? 'Resuming...' : 'Pausing...'
    return isPaused.value ? 'Resume' : 'Pause'
})

const statusColors = {
    draft: 'bg-gray-100 text-gray-600',
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    archived: 'bg-gray-100 text-gray-500',
}

function statusClass(status) {
    return statusColors[status] ?? statusColors.draft
}
</script>

<template>
    <div class="workflow-builder">
        <!-- Top toolbar -->
        <div class="workflow-builder__toolbar">
            <input
                v-model="workflowName"
                class="workflow-builder__name-input"
                type="text"
                placeholder="Workflow name..."
            />
            <span
                v-if="workflow"
                class="workflow-builder__status-badge"
                :class="statusClass(workflow.status)"
            >
                {{ workflow.status ?? 'draft' }}
            </span>

            <div class="workflow-builder__toolbar-actions">
                <button-warning
                    v-if="canTogglePause"
                    :disabled="isTogglingPause"
                    @click="togglePause"
                >
                    {{ pauseButtonLabel }}
                </button-warning>
                <button-secondary
                    :disabled="isSaving"
                    :state="saveState"
                    @click="saveDraft"
                >
                    {{ isSaving ? 'Saving...' : 'Save Draft' }}
                </button-secondary>
                <button-primary
                    :disabled="isPublishing"
                    :state="publishState"
                    @click="publish"
                >
                    {{ isPublishing ? 'Publishing...' : 'Publish' }}
                </button-primary>
            </div>
        </div>

        <!-- Main layout -->
        <div class="workflow-builder__body">
            <NodePalette />

            <div class="workflow-builder__canvas-wrapper">
                <WorkflowCanvas
                    @node-click="onNodeClick"
                    @pane-click="onPaneClick"
                    @drop="onDrop"
                />
                <FunnelOverlay
                    :workflow-id="Number(workflowId)"
                    :nodes="nodes"
                    :is-published="isPublished"
                />
            </div>

            <NodeConfigPanel
                v-if="selectedNode"
                :node="selectedNode"
                @update:config="onConfigUpdate"
            />
        </div>
    </div>
</template>

<style scoped>
.workflow-builder {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
}
.workflow-builder__toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    z-index: 10;
}
.workflow-builder__name-input {
    font-size: 16px;
    font-weight: 600;
    border: none;
    outline: none;
    color: #111827;
    flex: 1;
    min-width: 0;
    background: transparent;
}
.workflow-builder__name-input::placeholder {
    color: #9ca3af;
}
.workflow-builder__status-badge {
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;
}
.workflow-builder__toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}
.workflow-builder__body {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}
.workflow-builder__canvas-wrapper {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}
</style>
